import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Calendar, User, LogOut, LogIn } from "lucide-react";
import Logo from "./Logo";
import { useAuth, isAdmin, ROLE_LABELS } from "@/lib/AuthContext";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
} from "@/components/ui/navigation-menu";

// Menu ridotto: 6 voci principali + "Reti" annidato sotto "Chi siamo".
const MENU = [
  {
    label: "Chi siamo",
    to: "/chi-siamo",
    children: [
      { label: "Chi siamo", to: "/chi-siamo" },
      { label: "Il nostro approccio", to: "/il-nostro-approccio" },
      { label: "Reti e collaborazioni", to: "/reti-e-collaborazioni" },
    ],
  },
  { label: "Progetti", to: "/progetti" },
  { label: "Eventi", to: "/eventi" },
  { label: "Partecipa", to: "/partecipa" },
  { label: "Risorse", to: "/storie" },
  { label: "Contatti", to: "/contatti" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const nav = useNavigate();
  const { user, logout } = useAuth();

  const doLogout = () => { logout(); setUserMenuOpen(false); nav("/"); };

  return (
    <header className="sticky top-0 z-50 glass-header" data-testid="site-header">
      <div className="container-vv flex h-20 items-center justify-between gap-4">
        {/* LEFT: hamburger (mobile) + LOGO + Prossimi eventi CTA */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#292522]/10 bg-white text-[#292522] shrink-0"
            onClick={() => setOpen(true)}
            aria-label="Apri menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          </button>

          <Link to="/" className="shrink-0" data-testid="header-logo-link" aria-label="Vola Vola — Home">
            <Logo size="lg" />
          </Link>

          <button
            onClick={() => nav("/eventi")}
            className="hidden md:inline-flex btn-primary text-sm shrink-0"
            data-testid="cta-prossimi-eventi"
          >
            <Calendar className="h-4 w-4" strokeWidth={1.75} />
            Prossimi eventi
          </button>
        </div>

        {/* RIGHT: desktop menu + auth */}
        <div className="flex items-center gap-2">
          <nav className="hidden lg:flex items-center gap-1" aria-label="Menu principale">
            <NavigationMenu>
              <NavigationMenuList className="gap-0">
                {MENU.map((item) =>
                  item.children ? (
                    <NavigationMenuItem key={item.label}>
                      <NavigationMenuTrigger
                        className="bg-transparent text-[#292522] hover:bg-[#FFF1E8] data-[state=open]:bg-[#FFF1E8] data-[active]:bg-[#FFF1E8] text-sm font-medium"
                        data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                      >
                        {item.label}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-64 gap-1 p-3 bg-[#FFFCF8]">
                          {item.children.map((c) => (
                            <li key={c.label}>
                              <NavigationMenuLink asChild>
                                <Link
                                  to={c.to}
                                  className="block rounded-lg px-3 py-2 text-sm hover:bg-[#FFF1E8] hover:text-[#FF6100] transition-colors"
                                  data-testid={`nav-child-${c.label.toLowerCase().replace(/ /g, "-")}`}
                                >
                                  {c.label}
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  ) : (
                    <NavigationMenuItem key={item.label}>
                      <NavLink
                        to={item.to}
                        end={item.to === "/"}
                        className={({ isActive }) =>
                          `inline-flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-[#FFF1E8] hover:text-[#FF6100] ${isActive ? "text-[#FF6100]" : "text-[#292522]"}`
                        }
                        data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                      >
                        {item.label}
                      </NavLink>
                    </NavigationMenuItem>
                  )
                )}
              </NavigationMenuList>
            </NavigationMenu>
          </nav>

          {/* Auth badge */}
          {user ? (
            <div className="relative hidden md:block">
              <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="inline-flex items-center gap-2 rounded-full bg-[#FFF1E8] hover:bg-[#FF6100]/15 px-3 py-2 text-sm font-semibold text-[#292522]" data-testid="header-user-menu">
                <div className="h-6 w-6 rounded-full bg-[#FF6100] text-white text-xs flex items-center justify-center font-bold">
                  {(user.first_name || user.name || user.email)[0].toUpperCase()}
                </div>
                <span className="max-w-[110px] truncate">{user.first_name || user.name || user.email.split("@")[0]}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 z-50 w-56 bg-white rounded-2xl shadow-xl border border-[#292522]/8 overflow-hidden">
                    <div className="px-4 py-3 bg-[#FFF1E8] border-b border-[#292522]/8">
                      <div className="text-xs text-[#292522]/60">{user.email}</div>
                      <div className="text-xs font-semibold text-[#FF6100] mt-0.5">{ROLE_LABELS[user.role] || user.role}</div>
                    </div>
                    <Link to="/area-riservata" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-[#FFF1E8]" data-testid="user-menu-area">
                      <User className="h-4 w-4 inline mr-2" strokeWidth={1.75} /> Area riservata
                    </Link>
                    {isAdmin(user.role) && (
                      <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-[#FFF1E8]">
                        Pannello CMS
                      </Link>
                    )}
                    <button onClick={doLogout} className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#FFF1E8] text-[#B12A2A]" data-testid="user-menu-logout">
                      <LogOut className="h-4 w-4 inline mr-2" strokeWidth={1.75} /> Esci
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Link to="/accedi" className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[#FF6100]/30 hover:bg-[#FF6100]/10 px-3 py-2 text-sm font-semibold text-[#FF6100]" data-testid="header-accedi-btn">
              <LogIn className="h-4 w-4" strokeWidth={1.75} /> Accedi
            </Link>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-[#292522]/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[86%] max-w-sm bg-[#FFFCF8] shadow-2xl p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <Logo size="md" />
              <button
                onClick={() => setOpen(false)}
                aria-label="Chiudi menu"
                className="h-11 w-11 rounded-full border border-[#292522]/10 flex items-center justify-center"
                data-testid="mobile-menu-close"
              >
                <X className="h-5 w-5" strokeWidth={1.75} />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              {MENU.map((item) =>
                item.children ? (
                  <div key={item.label} className="mt-1">
                    <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest font-semibold text-[#292522]/50">{item.label}</div>
                    {item.children.map((c) => (
                      <Link key={c.label} to={c.to} onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg block text-base font-medium hover:bg-[#FFF1E8]" data-testid={`mobile-nav-${c.label.toLowerCase().replace(/ /g, "-")}`}>
                        {c.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link key={item.label} to={item.to} onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-base font-medium hover:bg-[#FFF1E8]" data-testid={`mobile-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}>
                    {item.label}
                  </Link>
                )
              )}
              {user ? (
                <>
                  <Link to="/area-riservata" onClick={() => setOpen(false)} className="mt-3 px-3 py-3 rounded-lg text-base font-medium bg-[#FFF1E8] text-[#FF6100]" data-testid="mobile-nav-area">
                    Area riservata ({ROLE_LABELS[user.role] || user.role})
                  </Link>
                  <button onClick={() => { doLogout(); setOpen(false); }} className="text-left px-3 py-3 rounded-lg text-sm text-[#B12A2A]">
                    Esci
                  </button>
                </>
              ) : (
                <>
                  <Link to="/accedi" onClick={() => setOpen(false)} className="mt-3 px-3 py-3 rounded-lg text-base font-medium border border-[#FF6100]/30 text-[#FF6100]" data-testid="mobile-nav-accedi">
                    Accedi
                  </Link>
                  <Link to="/registrati" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-base font-medium text-[#292522] hover:bg-[#FFF1E8]">
                    Registrati
                  </Link>
                </>
              )}
              <Link
                to="/eventi"
                onClick={() => setOpen(false)}
                className="btn-primary mt-4 justify-center"
                data-testid="mobile-cta-prossimi-eventi"
              >
                <Calendar className="h-4 w-4" strokeWidth={1.75} />
                Prossimi eventi
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
